import React, {useMemo} from "react";
import { useTranslation, Trans } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Input,
  Label,
  Hint,
  Heading,
  Tooltip,
  TooltipButton,
  Alert,
  GlyphInfo,
} from "@americanexpress/dls-react";
import {
  COUNTRY_WITH_DISCLAIMER,
  DAY_MONTH_BIRTH,
  EN,
  JAP,
  NEW_PA,
  EXISTING_PA,
  getDocMandatoryCountries,
  getVerificationWordMandatoryCountries,
  JOURNEY_TYPE,
  REGEX,
} from "../Common/constants";
import Upload from "../Import/Upload";
import {
  getCountryInfoText,
  getDisclaimerMap,
  getPAScreeningCountries,
  isBirthYearRequiredHelper,
} from "../Common/utils";
import { useFeatureFlag } from "../../util/oneXp/useFeatureFlag";
import { FEATURE_FLAG_NAMES } from "../../util/oneXp/featureFlagNames";

const APACForm = (props) => {
  const { t, i18n } = useTranslation("common");
  const { enabled: isEurzoneSingaporeChangesEnabled } = useFeatureFlag(
    FEATURE_FLAG_NAMES.GES_EUROZONE_SINGAPORE_COMPLIANCE,
  );

  const {
    language,
    submit,
    getDetails,
    modifyUser,
    users,
    updateSavobj,
    error,
    editSavobj,
    updateadd,
    user,
    setPaScreeningFiles,
    invalidYear,
    mailext,
  } = props;

  const {
    firstName,
    lastName,
    emailId,
    day,
    month,
    year,
    phoneNumber,
    paScreeningFiles,
    country,
    verificationWord,
    selfenroll,
    paAddress,
    paPostcode,
    paCountry,
    residenceAddress,
    vPinDay,
    vPinMonth,
  } = user;

  const { residenceAddressLine, cityOrSubrub, stateOrPrefecture, postCode } =
    residenceAddress;
  const isCountryWithDisclaimer = COUNTRY_WITH_DISCLAIMER.includes(country);
  const DISCLAIMER_MAP = getDisclaimerMap(t);
  const countryDisclaimer = DISCLAIMER_MAP[country];
  const isJapanNewPa = (selfenroll === NEW_PA && country === t("access.JAPAN"));
  const isNZAllPa = ((selfenroll === NEW_PA || selfenroll === EXISTING_PA) && country === t("access.NEWZEALAND"));
  const journeyType = modifyUser ? JOURNEY_TYPE.MODIFY : JOURNEY_TYPE.ADD;
  const isDocumentMandatory = getDocMandatoryCountries(t, journeyType, selfenroll).includes(country);
  const isBirthYearRequired = useMemo(() => {
    return isBirthYearRequiredHelper(t, selfenroll, country)
  }, [country, selfenroll, t]);


  const showNonAUOrNZ = (!modifyUser && country !== t("access.AUSTRALIA")) || isNZAllPa;
  const isVerificationWordMandatory = getVerificationWordMandatoryCountries(t).includes(country);
  return (
    <>
      <div id="country-APAC">
        <div className="row">
          <div className="col-md-6 col-xs-12">
            <Hint className="legal-1 pad-1-t pad-2-b">
              <p className="pad-1-b">{getCountryInfoText(country, t)}</p>
              {isCountryWithDisclaimer && countryDisclaimer && (
                <>
                  <p className="pad-1-b">{countryDisclaimer.doc}</p>
                  <p className="pad-1-b">
                    <Trans
                      i18nKey={countryDisclaimer.regulatory}
                      components={{
                        privacyLink: (
                          <a
                            href={t("privacy.australiaURL")}
                            target="_blank"
                            rel="noopener noreferrer"
                            tabIndex="-1"
                          >
                            {t("newuser.privacyLink")}
                          </a>
                        ),
                      }}
                    />
                  </p>
                </>
              )}
            </Hint>
          </div>
        </div>
        {selfenroll === NEW_PA && country !== t("access.INDIA") && (
          <div className="row">
            <div className="col-xs-12">
              <Hint className="legal-1 pad-1-b">
                {language === EN ? (
                  <>
                    <b>APAC Countries: </b>
                    {t("newuser.APACSetUpText")}
                  </>
                ) : (
                  t("newuser.setuptext")
                )}
              </Hint>
            </div>
          </div>
        )}
        {selfenroll === NEW_PA &&
          country === t("access.INDIA") &&
          language !== EN && (
            <div className="row">
              <div className="col-xs-12">
                <Hint className="legal-1 pad-1-b">
                  {t("newuser.setuptext")}
                </Hint>
              </div>
            </div>
          )}
        {selfenroll === NEW_PA &&
          country === t("access.INDIA") &&
          language === EN && (
            <div className="row">
              <div className="col-xs-12">
                <Hint className="legal-1 pad-1-b">
                  <b>India: </b>
                  {t("newuser.IndiaSetUpText")}
                </Hint>
              </div>
            </div>
          )}
        <div className="innerDiv pad-1-t">
          {language === EN ? (
            <>
              <div className="item3 pad-1-r">
                <Label className="label-3">{t("newuser.fn")}</Label>
                <Input
                  type="text"
                  value={firstName}
                  onChange={(event) => {
                    updateSavobj("firstName", event.target.value);
                  }}
                />

                {submit && firstName === "" && language === EN && (
                  <Alert status="error">
                    {t("newuser.fn")} {t("newuser.required")}
                  </Alert>
                )}
                {submit && firstName === "" && language === JAP && (
                  <Alert status="error">{t("newuser.firstNameRequired")}</Alert>
                )}
                {language === EN && !firstName.match(/^[a-zA-Z -]*$/) && (
                  <Alert status="error">{t("newuser.fnerror")}</Alert>
                )}
              </div>
              <div className="item3 pad-1-r">
                <Label className="label-3">{t("newuser.ln")}</Label>
                <Input
                  value={lastName}
                  onChange={(event) => {
                    updateSavobj("lastName", event.target.value);
                  }}
                />

                {submit && lastName === "" && language === EN && (
                  <Alert status="error">
                    {t("newuser.ln")} {t("newuser.required")}
                  </Alert>
                )}
                {submit && lastName === "" && language === JAP && (
                  <Alert status="error">{t("newuser.lastNameRequired")}</Alert>
                )}
                {language === EN && !lastName.match(/^[a-zA-Z -]*$/) && (
                  <Alert status="error">{t("newuser.lnerror")} </Alert>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="item3 pad-1-r">
                <Label className="label-3">{t("newuser.ln")}</Label>
                <Input
                  value={lastName}
                  onChange={(event) => {
                    updateSavobj("lastName", event.target.value);
                  }}
                />
                {submit && lastName === "" && language === EN && (
                  <Alert status="error">
                    {t("newuser.ln")} {t("newuser.required")}
                  </Alert>
                )}
                {submit && lastName === "" && language === JAP && (
                  <Alert status="error">{t("newuser.lastNameRequired")}</Alert>
                )}
                {language === EN && !lastName.match(/^[a-zA-Z -]*$/) && (
                  <Alert status="error">{t("newuser.lnerror")} </Alert>
                )}
              </div>
              <div className="item3 pad-1-r ">
                <Label className="label-3">{t("newuser.fn")}</Label>
                <Input
                  type="text"
                  value={firstName}
                  onChange={(event) => {
                    updateSavobj("firstName", event.target.value);
                  }}
                />
                {submit && firstName === "" && language === EN && (
                  <Alert status="error">
                    {t("newuser.fn")} {t("newuser.required")}
                  </Alert>
                )}
                {submit && firstName === "" && language === JAP && (
                  <Alert status="error">{t("newuser.firstNameRequired")}</Alert>
                )}
                {language === EN && !firstName.match(/^[a-zA-Z -]*$/) && (
                  <Alert status="error">{t("newuser.fnerror")}</Alert>
                )}
              </div>
            </>
          )}

          <div className="item pad-1-r">
            <Label className="label-3">{t("newuser.email")}</Label>
            <Input
              value={emailId}
              onChange={(event) => {
                updateSavobj("emailId", event.target.value);
              }}
              onBlur={getDetails}
            />
            <Hint className="legal-1">
              {t("newuser.emailinfo")}{" "}
              <Link
                to="/privacy-links"
                target="_blank"
                rel="noopener noreferrer"
                tabIndex="-1"
              >
                {t("newuser.link")}
              </Link>{" "}
              {t("newuser.emailinfo2")}
            </Hint>
            {submit && emailId === "" && language === EN && (
              <Alert status="error">
                {t("newuser.email")} {t("newuser.required")}
              </Alert>
            )}
            {submit && emailId === "" && language === JAP && (
              <Alert status="error">{t("newuser.emailRequired")}</Alert>
            )}
            {emailId !== "" &&
              (error || submit) &&
              mailext !== undefined &&
              emailId.substring(emailId.indexOf("@") + 1) !== mailext && (
                <Alert>
                  Users should belong to the same company for multi-user
                  submission
                </Alert>
              )}
            {emailId !== "" &&
              (error || submit) &&
              !emailId.match(
                /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9_-]+(?:\.[a-zA-Z0-9_-]+)*$/
              ) && <Alert status="error">{t("newuser.emailerror")}</Alert>}
          </div>

          <div
            className={
              modifyUser ? "item pad-1-r" : "item margin-align pad-1-r pad-4-t"
            }
          >
            <Label className="label-3">{t("newuser.phno")}</Label>
            <Input
              value={phoneNumber}
              onChange={(event) => {
                updateSavobj("phoneNumber", event.target.value);
              }}
            />
            <Hint>{t("common.TelephoneInfoMessage")}</Hint>
            {submit && phoneNumber === "" && language === EN && (
              <Alert status="error">
                {t("newuser.phno")} {t("newuser.required")}
              </Alert>
            )}
            {submit && phoneNumber === "" && language === JAP && (
              <Alert status="error">{t("newuser.phoneNumberRequired")}</Alert>
            )}
            {!phoneNumber.match(/^[0-9+-]*$/) && (
              <Alert status="error">{t("newuser.phnerror")}</Alert>
            )}
          </div>
        </div>

        {country === t("access.AUSTRALIA") && (
          <>
            <div className="row">
              <div className="col-md-4 col-xs-12 pad-2-t">
                <Label>{t("newuser.full add")}</Label>
                <Input
                  value={paAddress}
                  onChange={(event) => {
                    updateSavobj("paAddress", event.target.value);
                  }}
                />
                {submit && paAddress === "" && language === EN && (
                  <Alert status="error">
                    {t("newuser.full add")} {t("newuser.required")}
                  </Alert>
                )}

                {submit && paAddress === "" && language === JAP && (
                  <Alert status="error">
                    PA Residential Address Required
                  </Alert>
                )}
              </div>
              <div className="col-md-4 col-xs-12 pad-2-tb">
                <Label>{t("newuser.postalCode")}</Label>
                <Input
                  value={paPostcode}
                  onChange={(event) => {
                    updateSavobj("paPostcode", event.target.value);
                  }}
                />
                {submit && paPostcode === "" && language === EN && (
                  <Alert status="error">
                    {t("newuser.postalCode")} {t("newuser.required")}
                  </Alert>
                )}

                {submit && paPostcode === "" && language === JAP && (
                  <Alert status="error">
                    Postal Code Required
                  </Alert>
                )}

                {paPostcode !== "" && !paPostcode.match(REGEX.POSTAL_CODE) && (
                  <Alert status="error">
                    {t("newuser.postcodeerror")}
                  </Alert>
                )}
              </div>
              <div className="col-md-4 col-xs-12 pad-2-tb">
                <Label>{t("newuser.countryResidence")}</Label>
                <Input
                  value={paCountry}
                  onChange={(event) => {
                    updateSavobj("paCountry", event.target.value);
                  }}
                />
                {submit && paCountry === "" && language === EN && (
                  <Alert status="error">
                    {t("newuser.countryResidence")} {t("newuser.required")}
                  </Alert>
                )}

                {submit && paCountry === "" && language === JAP && (
                  <Alert status="error">
                    Country Required
                  </Alert>
                )}
                {paCountry !== "" && !paCountry.match(REGEX.COUNTRY_RESIDENCE) && (
                  <Alert status="error">
                    {t("newuser.cnoberror")}
                  </Alert>
                )}
              </div>
            </div>
            <div className="pad-2-t">
              <div className="innerDiv">
                <div
                  className={
                    i18n.language === JAP ? "item6 pad-1-r" : "item6 pad-1-r"
                  }
                >
                  <Label className="label-3">{t("newuser.day")}</Label>
                  <Input
                    placeholder={"DD"}
                    value={day}
                    onChange={(event) => {
                      updateSavobj("day", event.target.value);
                    }}
                    maxLength="2"
                  />
                  {submit && day === "" && language === EN && (
                    <Alert status="error">
                      {t("newuser.day")} {t("newuser.required")}
                    </Alert>
                  )}
                  {submit && day === "" && language === JAP && (
                    <Alert status="error">
                      {t("newuser.dateofBirthRequired")}
                    </Alert>
                  )}

                  {(!day.match(/^[0-9]*$/) ||
                    day.length > 2 ||
                    parseInt(day) > 31 ||
                    parseInt(day) <= 0) && (
                      <Alert status="error">{t("newuser.dayvalid")}</Alert>
                    )}
                </div>
                <div className="item6 pad-1-r">
                  <Label className="label-3">{t("newuser.month")}</Label>
                  <Input
                    placeholder={"MM"}
                    value={month}
                    onChange={(event) => {
                      updateSavobj("month", event.target.value);
                    }}
                    maxLength="2"
                  />

                  {submit && month === "" && language === JAP && (
                    <Alert status="error">
                      {t("newuser.monthofBirthRequired")}
                    </Alert>
                  )}
                  {submit && month === "" && language === EN && (
                    <Alert status="error">
                      {t("newuser.month")} {t("newuser.required")}
                    </Alert>
                  )}
                  {(!month.match(/^[0-9]*$/) ||
                    month.length > 2 ||
                    parseInt(month) > 12 ||
                    month === "00") && (
                      <Alert status="error">{t("newuser.monthvalid")}</Alert>
                    )}
                </div>
                <div className="item6 pad-1-r">
                  <Label className="label-3">{t("newuser.year")}</Label>
                  <Input
                    type="number"
                    placeholder={"YYYY"}
                    value={year}
                    onChange={(event) => {
                      updateSavobj("year", event.target.value);
                    }}
                    maxLength="4"
                  />

                  {submit && year === "" && language === EN && (
                    <Alert status="error">
                      {t("newuser.year")} {t("newuser.required")}
                    </Alert>
                  )}
                  {submit && year === "" && language === JAP && (
                    <Alert status="error">
                      {t("newuser.yearofBirthRequired")}
                    </Alert>
                  )}

                  {(invalidYear ||
                    year.length > 4) && (
                      <Alert status="error">
                        {t("newuser.validYear")}
                      </Alert>
                    )}
                </div>

                {getPAScreeningCountries(t).includes(country) && (
                  <div className="col-md-12">
                    <Upload
                      country={country}
                      getFile={setPaScreeningFiles}
                      files={paScreeningFiles}
                    />
                    {submit && getPAScreeningCountries(t).includes(country) && isDocumentMandatory && paScreeningFiles.length === 0 && (
                      <Alert status="error">
                        {t("newuser.documentRequired")}
                      </Alert>
                    )}
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {isEurzoneSingaporeChangesEnabled && country === t("access.SINGAPORE") && (
          <>
            <div className="row">
              <div className="col-md-4 col-xs-12 pad-2-t">
                <Label>{t("newuser.full add")}</Label>
                <Input
                  value={paAddress}
                  onChange={(event) => {
                    updateSavobj("paAddress", event.target.value);
                  }}
                />
              </div>
              <div className="col-md-4 col-xs-12 pad-2-tb">
                <Label>{t("newuser.postalCode")}</Label>
                <Input
                  value={paPostcode}
                  onChange={(event) => {
                    updateSavobj("paPostcode", event.target.value);
                  }}
                />
                {paPostcode !== "" && !paPostcode.match(REGEX.POSTAL_CODE) && (
                  <Alert status="error">
                    {t("newuser.postcodeerror")}
                  </Alert>
                )}
              </div>
              <div className="col-md-4 col-xs-12 pad-2-tb">
                <Label>{t("newuser.countryResidence")}</Label>
                <Input
                  value={paCountry}
                  onChange={(event) => {
                    updateSavobj("paCountry", event.target.value);
                  }}
                />
                {paCountry !== "" && !paCountry.match(REGEX.COUNTRY_RESIDENCE) && (
                  <Alert status="error">
                    {t("newuser.cnoberror")}
                  </Alert>
                )}
              </div>
            </div>
          </>
        )}

        {showNonAUOrNZ && (
          <div className="pad-2-t">
            <div className="innerDiv">
              <div
                className={
                  i18n.language === JAP ? "item6 pad-1-r" : "item6 pad-1-r"
                }
              >
                <Label className="label-3">{t("newuser.day")}</Label>
                <Input
                  placeholder={"DD"}
                  value={day}
                  onChange={(event) => {
                    updateSavobj("day", event.target.value);
                  }}
                  maxLength="2"
                />
                {submit && day === "" && language === EN && (
                  <Alert status="error">
                    {t("newuser.day")} {t("newuser.required")}
                  </Alert>
                )}
                {submit && day === "" && language === JAP && (
                  <Alert status="error">
                    {t("newuser.dateofBirthRequired")}
                  </Alert>
                )}

                {(!day.match(/^[0-9]*$/) ||
                  day.length > 2 ||
                  parseInt(day) > 31 ||
                  parseInt(day) <= 0) && (
                    <Alert status="error">{t("newuser.dayvalid")}</Alert>
                  )}
              </div>
              <div className="item6 pad-1-r">
                <Label className="label-3">{t("newuser.month")}</Label>
                <Input
                  placeholder={"MM"}
                  value={month}
                  onChange={(event) => {
                    updateSavobj("month", event.target.value);
                  }}
                  maxLength="2"
                />

                {submit && month === "" && language === JAP && (
                  <Alert status="error">
                    {t("newuser.monthofBirthRequired")}
                  </Alert>
                )}
                {submit && month === "" && language === EN && (
                  <Alert status="error">
                    {t("newuser.month")} {t("newuser.required")}
                  </Alert>
                )}
                {(!month.match(/^[0-9]*$/) ||
                  month.length > 2 ||
                  parseInt(month) > 12 ||
                  month === "00") && (
                    <Alert status="error">{t("newuser.monthvalid")}</Alert>
                  )}
              </div>

              {isBirthYearRequired && (
                <div className="item6 pad-1-r">
                  <Label className="label-3">{t("newuser.year")}</Label>
                  <Input
                    type="number"
                    placeholder={"YYYY"}
                    value={year}
                    onChange={(event) => {
                      updateSavobj("year", event.target.value);
                    }}
                    maxLength="4"
                  />

                  {submit && year === "" && language === EN && (
                    <Alert status="error">
                      {t("newuser.year")} {t("newuser.required")}
                    </Alert>
                  )}
                  {submit && year === "" && language === JAP && (
                    <Alert status="error">
                      {t("newuser.yearofBirthRequired")}
                    </Alert>
                  )}

                  {(invalidYear ||
                    (year.length === 4 && parseInt(year) < 1900) ||
                    parseInt(year) > 2004) && (
                      <Alert status="error">
                        {t("newUserUK.validYearMessage")}
                      </Alert>
                    )}
                </div>
              )}

              {language === EN &&
                selfenroll === NEW_PA &&
                isBirthYearRequired && (
                  <div className="row pad-1-t">
                    <div className="col-md-6">
                      <Hint>{t("newuser.dobnotewithyear")}</Hint>
                    </div>
                  </div>
                )}

              {(isJapanNewPa || isNZAllPa) && (
                <>
                  <div className="innerDiv pad-1-t pad-2-b">
                    <div className="item2 margin-0">
                      <Label className="label-3 item">
                        {t("newuser.full add")} <br />
                        {t("newuser.full add2")}
                      </Label>
                      <Input
                        value={residenceAddressLine}
                        onChange={(event) => {
                          updateadd(
                            "residenceAddressLine",
                            event.target.value
                          );
                        }}
                      />

                      {submit &&
                        residenceAddressLine === "" &&
                        language === EN && (
                          <Alert status="error">
                            {t("newuser.full add")} {t("newuser.required")}
                          </Alert>
                        )}
                      {submit &&
                        residenceAddressLine === "" &&
                        language === JAP && (
                          <Alert status="error">
                            {t("newuser.addressRequired")}
                          </Alert>
                        )}
                    </div>
                  </div>

                  <div className="innerDiv pad-1-t pad-2-b">
                    <div className="item3 pad-1-r">
                      <Label className="label-3">{t("newuser.City")}</Label>
                      <Input
                        value={cityOrSubrub}
                        onChange={(event) => {
                          updateadd("cityOrSubrub", event.target.value);
                        }}
                      />
                      {submit && cityOrSubrub === "" && language === EN && (
                        <Alert status="error">
                          {t("newuser.City")} {t("newuser.required")}
                        </Alert>
                      )}
                      {submit &&
                        cityOrSubrub === "" &&
                        language === JAP && (
                          <Alert status="error">
                            {t("newuser.cityRequired")}
                          </Alert>
                        )}
                    </div>

                    <div className="item3 pad-1-r">
                      <Label className="label-3">{t("newuser.State")}</Label>
                      <Input
                        value={stateOrPrefecture}
                        onChange={(event) => {
                          updateadd("stateOrPrefecture", event.target.value);
                        }}
                      />
                      {submit &&
                        stateOrPrefecture === "" &&
                        language === EN && (
                          <Alert status="error">
                            {t("newuser.State")} {t("newuser.required")}
                          </Alert>
                        )}
                      {submit &&
                        stateOrPrefecture === "" &&
                        language === JAP && (
                          <Alert status="error">
                            {t("newuser.prefectureRequired")}
                          </Alert>
                        )}
                    </div>
                    <div className="item3 pad-1-r">
                      <Label className="label-3">
                        {t("newuser.Postcode")}
                      </Label>
                      <Input
                        value={postCode}
                        onChange={(event) => {
                          updateadd("postCode", event.target.value);
                        }}
                      />
                      {submit && postCode === "" && language === EN && (
                        <Alert status="error">
                          {t("newuser.Postcode")} {t("newuser.required")}
                        </Alert>
                      )}
                      {submit && postCode === "" && language === JAP && (
                        <Alert status="error">
                          {t("newuser.zipRequired")}
                        </Alert>
                      )}
                    </div>
                  </div>
                </>
              )}

              {(country === t("access.NEWZEALAND") ||
                country === t("access.SINGAPORE") ||
                country === t("access.HONGKONG") ||
                country === t("access.INDIA") ||
                country === t("access.TAIWAN") ||
                country === t("access.THAILAND")) && (
                  <div className="item3 pad-1-r">
                    <Label className="label-3">{t("newuser.vword")}</Label>
                    <div className="veriTooltip">
                      <Tooltip
                        id="tooltip-default-top"
                        triggerElement={
                          <TooltipButton icon={<GlyphInfo />} tabIndex="-1" />
                        }
                        placement="top"
                      >
                        {t("newuser.vwordinfo")}
                      </Tooltip>
                    </div>
                    <Input
                      id="verificationWord"
                      placeholder={lastName.length > 3 ? lastName : firstName}
                      value={verificationWord}
                      onChange={(event) => {
                        updateSavobj("verificationWord", event.target.value);
                      }}
                    />
                    {submit && isVerificationWordMandatory && verificationWord === "" && (
                      <Alert status="error">
                        {t("newuser.vwordmandatory")}
                      </Alert>
                    )}
                    {language === EN &&
                      verificationWord.length > 0 &&
                      !REGEX.NAME_ALPHA.test(verificationWord) && (
                        <Alert status="error">{t("newuser.vwordvalid")}</Alert>
                      )}
                  </div>
                )}

              {language === EN &&
                (selfenroll === EXISTING_PA ||
                  (!isBirthYearRequired && (
                      <div className="row pad-1-t">
                        <div className="col-md-6">
                          <Hint>{t("newuser.dobnotewithoutyear")}</Hint>
                        </div>
                      </div>
                    )))}

              {selfenroll === EXISTING_PA &&
                country === t("access.NEWZEALAND") && (
                  <div className="col-md-12">
                    <Upload
                      country={country}
                      getFile={setPaScreeningFiles}
                      files={paScreeningFiles}
                    />
                    {submit && getPAScreeningCountries(t).includes(country) && isDocumentMandatory && paScreeningFiles.length === 0 && (
                      <Alert status="error">
                        {t("newuser.documentRequired")}
                      </Alert>
                    )}
                  </div>
                )}

              {selfenroll === NEW_PA &&
                (country === t("access.JAPAN") ||
                  country === t("access.NEWZEALAND")) && (
                  <>
                    <div
                      className="item3 pad-1-r"
                      style={{ marginTop: "-10px" }}
                    >
                      <Label className="label-3 pad-1-t">{t("access.vpinday")}</Label>
                      <span
                        style={{
                          float: "right",
                          marginTop: "1px",
                        }}
                      >
                        <Tooltip
                          id="tooltip-default-top"
                          triggerElement={
                            <TooltipButton icon={<GlyphInfo />} tabIndex="-1" />
                          }
                          placement="top"
                        >
                          {t("access.daymonth")}
                        </Tooltip>
                      </span>

                      <Input
                        placeholder={day.length > 0 && day}
                        value={vPinDay}
                        onChange={(event) => {
                          updateSavobj("vPinDay", event.target.value);
                        }}
                      />

                      {(!vPinDay.match(/^[0-9]*$/) ||
                        vPinDay.length > 2 ||
                        parseInt(vPinDay) > 31 ||
                        parseInt(vPinDay) <= 0) && (
                          <Alert status="error">Enter a valid date</Alert>
                        )}
                    </div>
                    <div className="item3 pad-1-r">
                      <Label className="label-3">{t("access.vpinmonth")}</Label>

                      <Input
                        placeholder={month.length > 0 && month}
                        value={vPinMonth}
                        onChange={(event) => {
                          updateSavobj("vPinMonth", event.target.value);
                        }}
                      />

                      {(!vPinMonth.match(/^[0-9]*$/) ||
                        vPinMonth.length > 2 ||
                        parseInt(vPinMonth) > 12 ||
                        vPinMonth === "00") && (
                          <Alert status="error">
                            {t("newuser.vword")}
                            {t("newuser.required")}
                          </Alert>
                        )}
                      {language === EN &&
                        (!users[users.length - 1].verificationWord.match(
                          /^[a-zA-Z]*$/
                        ) ||
                          users[users.length - 1].verificationWord.length <
                          0) && (
                          <Alert status="error">
                            {t("newuser.vwordvalid")}
                          </Alert>
                        )}
                    </div>

                    {getPAScreeningCountries(t).includes(country) && (
                      <div className="col-md-12">
                        <Upload
                          country={country}
                          getFile={setPaScreeningFiles}
                          files={paScreeningFiles}
                        />
                        {submit && getPAScreeningCountries(t).includes(country) && isDocumentMandatory && paScreeningFiles.length === 0 && (
                          <Alert status="error">
                            {t("newuser.documentRequired")}
                          </Alert>
                        )}
                      </div>
                    )}
                  </>
                )}

              {users[users.length - 1].selfenroll === NEW_PA &&
                (users[users.length - 1].country === t("access.JAPAN") ||
                  (users[users.length - 1].country ===
                    t("access.NEWZEALAND") && (
                      <>
                        <div className="item3 pad-1-r">
                          <Label className="label-3">{t("access.vpinday")}</Label>
                          <span
                            style={{
                              float: "right",
                            }}
                          >
                            <Tooltip
                              id="tooltip-default-top"
                              triggerElement={
                                <TooltipButton
                                  icon={<GlyphInfo />}
                                  tabIndex="-1"
                                />
                              }
                              placement="top"
                            >
                              {DAY_MONTH_BIRTH}
                            </Tooltip>
                          </span>

                          <Input
                            placeholder={
                              users[users.length - 1].day.length > 0 &&
                              users[users.length - 1].day
                            }
                            value={
                              index === -1
                                ? users[users.length - 1].vPinDay
                                : vPinDay
                            }
                            onChange={(event) => {
                              index === -1
                                ? updateSavobj(
                                  "vPinDay",
                                  event.target.value,
                                  users.length - 1
                                )
                                : editSavobj("day", event.target.value);
                            }}
                          />
                          {submit && users[users.length - 1].vPinDay === "" && (
                            <Alert status="error">{t("access.vpindayreq")}</Alert>
                          )}

                          {(!users[users.length - 1].vPinDay.match(/^[0-9]*$/) ||
                            users[users.length - 1].vPinDay.length > 2 ||
                            parseInt(users[users.length - 1].vPinDay) > 31 ||
                            parseInt(users[users.length - 1].vPinDay) <= 0) && (
                              <Alert status="error">{t("newuser.dayvalid")}</Alert>
                            )}
                        </div>
                        <div className="item3 pad-1-r">
                          <Label className="label-3">
                            {t("access.vpinmonth")}
                          </Label>

                          <Input
                            placeholder={
                              users[users.length - 1].month.length > 0 &&
                              users[users.length - 1].month
                            }
                            value={
                              index === -1
                                ? users[users.length - 1].vPinMonth
                                : vPinMonth
                            }
                            onChange={(event) => {
                              index === -1
                                ? updateSavobj(
                                  "vPinMonth",
                                  event.target.value,
                                  users.length - 1
                                )
                                : editSavobj("month", event.target.value);
                            }}
                          />
                          {submit &&
                            users[users.length - 1].vPinMonth.length === 0 && (
                              <Alert status="error">
                                {t("access.vpinmonthreq")}
                              </Alert>
                            )}
                          {(!users[users.length - 1].vPinMonth.match(
                            /^[0-9]*$/
                          ) ||
                            users[users.length - 1].vPinMonth.length > 2 ||
                            parseInt(users[users.length - 1].vPinMonth) > 12 ||
                            users[users.length - 1].vPinMonth === "00") && (
                              <Alert status="error">
                                {t("newuser.monthvalid")}
                              </Alert>
                            )}
                        </div>
                      </>
                    )))}
            </div>
          </div>
        )}

        {isEurzoneSingaporeChangesEnabled && country === t("access.SINGAPORE") &&
          getPAScreeningCountries(t).includes(country) && (
            <div className="col-md-12 pad-2-t">
              <Upload
                country={country}
                getFile={setPaScreeningFiles}
                files={paScreeningFiles}
              />
              {submit && getPAScreeningCountries(t).includes(country) && isDocumentMandatory && paScreeningFiles.length === 0 && (
                <Alert status="error">
                  {t("newuser.documentRequired")}
                </Alert>
              )}
            </div>
          )}
      </div>
    </>
  );
};

export default APACForm;
